'use client';

import type { ProductCard } from '@repo/shopify';
import { useEffect, useMemo, useState } from 'react';
import { ProductGridSkeleton } from '@/components/product/product-grid-client';
import { fetchProductBlockProducts, type ProductBlockRequest } from './product-block-products';
import { getSectionSpacing } from './section-spacing';
import { TabbedProductsClient } from './tabbed-products-client';

interface TabbedProductsBlockTab {
	label: string;
	request: ProductBlockRequest;
}

interface ResolvedTab {
	label: string;
	products: ProductCard[];
	ratings: (number | null)[];
	reviewCounts: (number | null)[];
}

export function TabbedProductsBlockClient({
	heading,
	sectionId,
	tabs,
	initialTabs,
	sectionSpacing,
	moneyLocale,
	// Server-rendered `initialTabs` are authoritative; only live preview refetches.
	fetchOnMount = false,
}: {
	heading?: string;
	sectionId?: string;
	tabs: TabbedProductsBlockTab[];
	initialTabs?: ResolvedTab[];
	sectionSpacing?: string;
	moneyLocale?: string;
	fetchOnMount?: boolean;
}) {
	const [resolvedTabs, setResolvedTabs] = useState<ResolvedTab[] | null>(initialTabs ?? null);
	const tabsKey = useMemo(() => JSON.stringify(tabs), [tabs]);

	useEffect(() => {
		if (fetchOnMount === false) {
			setResolvedTabs(initialTabs ?? null);
			return;
		}

		const controller = new AbortController();
		const nextTabs = JSON.parse(tabsKey) as TabbedProductsBlockTab[];
		setResolvedTabs(initialTabs ?? null);

		void Promise.all(
			nextTabs.map(async (tab) => {
				const products = await fetchProductBlockProducts(tab.request, controller.signal);

				return {
					label: tab.label,
					products,
					ratings: products.map(() => null),
					reviewCounts: products.map(() => null),
				};
			}),
		)
			.then((nextResolvedTabs) => {
				if (!controller.signal.aborted) {
					setResolvedTabs(nextResolvedTabs.filter((tab) => tab.products.length > 0));
				}
			})
			.catch(() => {
				if (!controller.signal.aborted) {
					setResolvedTabs([]);
				}
			});

		return () => controller.abort();
	}, [fetchOnMount, initialTabs, tabsKey]);

	if (tabs.length === 0) return null;
	// Published path never refetches: an empty server result renders nothing
	// rather than a skeleton baked into the static HTML.
	if (resolvedTabs === null && !fetchOnMount) return null;

	if (resolvedTabs === null) {
		return (
			<section
				id={sectionId}
				className={getSectionSpacing(sectionSpacing, { hasHeader: Boolean(heading) })}
			>
				<div className="container-page">
					{heading && <h2 className="mb-6 text-3xl font-black tracking-tight">{heading}</h2>}
					<div className="mb-6 flex flex-wrap gap-2">
						{tabs.map((tab) => (
							<div
								key={tab.label}
								className="rounded-full bg-neutral-100 px-4 py-2 text-sm font-semibold text-neutral-500"
							>
								{tab.label}
							</div>
						))}
					</div>
					<ProductGridSkeleton count={4} />
				</div>
			</section>
		);
	}

	if (resolvedTabs.length === 0) return null;

	return (
		<TabbedProductsClient
			heading={heading}
			sectionId={sectionId}
			tabs={resolvedTabs}
			enableReviews={false}
			moneyLocale={moneyLocale}
			sectionSpacing={sectionSpacing}
		/>
	);
}
